import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Table, message, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { queryOntologyObject, QueryOntologyObjectRequest } from '@/api/ontologyQueryApi';

interface OntologyObjectQueryModalProps {
  visible: boolean;
  onCancel: () => void;
  ontologyId: number;
  conceptName: string;
  attributes: string[];
}

interface Constraint {
  id: string;
  attribute: string;
  operator: string;
  value: string;
}

const OPERATORS = [
  { label: '=', value: '=' },
  { label: '!=', value: '!=' },
  { label: '>', value: '>' },
  { label: '<', value: '<' },
  { label: '>=', value: '>=' },
  { label: '<=', value: '<=' },
  { label: 'LIKE', value: 'like' },
];

const OntologyObjectQueryModal: React.FC<OntologyObjectQueryModalProps> = ({
  visible,
  onCancel,
  ontologyId,
  conceptName,
  attributes,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [constraints, setConstraints] = useState<Constraint[]>([]);
  const [queryResult, setQueryResult] = useState<any>(null);
  const [limit, setLimit] = useState<number>(10);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setConstraints([]);
      setQueryResult(null);
      setLimit(10);
    }
  }, [visible, form]);

  const addConstraint = () => {
    const newConstraint: Constraint = {
      id: Date.now().toString(),
      attribute: '',
      operator: '=',
      value: '',
    };
    setConstraints([...constraints, newConstraint]);
  };

  const removeConstraint = (id: string) => {
    setConstraints(constraints.filter(c => c.id !== id));
  };

  const updateConstraint = (id: string, field: keyof Constraint, value: string) => {
    setConstraints(constraints.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const handleQuery = async () => {
    try {
      const values = await form.validateFields();
      
      setLoading(true);
      
      const params: QueryOntologyObjectRequest = {
        ontologyId,
        conceptName,
        selectAttributes: values.selectAttributes,
        constraints: constraints
          .filter(c => c.attribute && c.value)
          .map(c => ({
            attribute: c.attribute,
            operator: c.operator,
            value: c.value,
          })),
        limit,
      };

      const result = await queryOntologyObject(params);
      setQueryResult(result);
      message.success('查询成功');
    } catch (error: any) {
      console.error('查询失败:', error);
      message.error(error?.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  const columns = queryResult?.data && queryResult.data.length > 0
    ? Object.keys(queryResult.data[0]).map(key => ({
        title: key,
        dataIndex: key,
        key,
        ellipsis: true,
      }))
    : [];

  return (
    <Modal
      title={`查询本体对象数据 - ${conceptName}`}
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="close" onClick={onCancel}>
          关闭
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="选择属性"
          name="selectAttributes"
          rules={[{ required: false }]}
        >
          <Select
            mode="multiple"
            placeholder="请选择要查询的属性（不选则查询所有）"
            options={attributes.map(attr => ({ label: attr, value: attr }))}
            allowClear
          />
        </Form.Item>

        <Form.Item label="查询条件">
          <div className="space-y-2">
            {constraints.map((constraint) => (
              <div key={constraint.id} className="flex gap-2 items-center">
                <Select
                  style={{ width: 200 }}
                  placeholder="选择属性"
                  value={constraint.attribute}
                  onChange={(value) => updateConstraint(constraint.id, 'attribute', value)}
                  options={attributes.map(attr => ({ label: attr, value: attr }))}
                />
                <Select
                  style={{ width: 100 }}
                  value={constraint.operator}
                  onChange={(value) => updateConstraint(constraint.id, 'operator', value)}
                  options={OPERATORS}
                />
                <Input
                  style={{ flex: 1 }}
                  placeholder="输入值"
                  value={constraint.value}
                  onChange={(e) => updateConstraint(constraint.id, 'value', e.target.value)}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeConstraint(constraint.id)}
                />
              </div>
            ))}
            <Button
              type="dashed"
              onClick={addConstraint}
              icon={<PlusOutlined />}
              block
            >
              添加查询条件
            </Button>
          </div>
        </Form.Item>

        <Form.Item label="限制条数">
          <InputNumber
            min={1}
            max={1000}
            value={limit}
            onChange={(value) => setLimit(value || 10)}
            style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" onClick={handleQuery} loading={loading} block>
            执行查询
          </Button>
        </Form.Item>
      </Form>

      {queryResult && (
        <div className="mt-4">
          <div className="mb-2 text-sm text-gray-600">
            <div>生成的SQL: <code className="bg-gray-100 px-2 py-1 rounded">{queryResult.generatedTypeQL}</code></div>
            <div className="mt-1">查询结果: 共 {queryResult.total} 条数据，耗时 {queryResult.executionTime}ms</div>
          </div>
          <Table
            dataSource={queryResult.data}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content', y: 400 }}
            size="small"
            rowKey={(_, index) => index?.toString() || '0'}
          />
        </div>
      )}
    </Modal>
  );
};

export default OntologyObjectQueryModal;
